import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import _ from 'lodash';

import { makeCanonicalTagObj, makeHreflangTagArr } from '@/utils';
import ProductHeroOverview from './ProductHeroOverview/ProductHeroOverview';
import ProductInstruction from './ProductInstruction/ProductInstruction';
import ProductIngredients from '../../components/ProductIngredients/ProductIngredients';
import ProductBenefits from '../../components/ProductBenefits/ProductBenefits';
import ProductReviews from '../../components/ProductReviews/ProductReviews';
import ProductRecommendation from '../../components/ProductRecommendation/ProductRecommendation';
import ProductHowTo from '../../components/ProductHowTo/ProductHowTo';
import BreadCrumbMenu from '../../components/BreadCrumbMenus/';
import Placeholder from '../../components/Placeholder';
import { TABLET_P } from '../../constants';

/**
 * 商品詳細画面
 *   TOP > PRODUCTS > カテゴリ > LINE 「さらに見る」押下時に呼ばれる
 */
const ProductPage = ({ copy, locale, data, location, menuInfo }) => { // eslint-disable-line
  // パンくずリスト > カテゴリ表示用 (サブカテゴリがある場合に使用する)
  let labelWithSubCategory = null;
  // パンくずリスト > カテゴリ表示用 (サブカテゴリがない場合に使用する)
  let labelWithoutSubCategory = null;
  // パンくずリスト > サブカテゴリ表示用
  let subCategoryLabel = null;
  // パンくずリスト > LINE 表示用
  let lineLabel = null;
  // サブカテゴリのパスを設定するのに使用する
  let subCategoryPath = null;
  // LINE のパス (例) THE GROOMING > HAIR & SCALP CARE のパス)
  let linePath = null;

  if (data && data.range) {
    _.forEach(_.get(menuInfo, 'children'), ({ label, children }) => {
      _.forEach(children, item => {
        // 該当: サブカテゴリを持たないもの
        // 該当する例: HAIR COLOR > COLORMUSE
        if (!_.get(item, 'children')) {
          if (
            !!(
              _.get(item, 'label') &&
              _.get(data, 'key') &&
              item.label.toLowerCase() === data.range.key.toLowerCase()
            ) ||
            !!(_.get(item, 'slug') && _.get(data, 'slug') && item.slug.toLowerCase() === data.range.slug.toLowerCase())
          ) {
            linePath = `/${locale}/range/${label
              .replace('/', '-')
              .replace(' ', '-')
              .toLowerCase()}/${data.range.slug}`;
            labelWithoutSubCategory = label.toUpperCase();
          }
        }
        // 該当: サブカテゴリを持つもの
        else {
          // 該当: HAIR CARE > SUBLIMIC、HAIR CARE > THE HAIR CARE
          if (_.find(item.children, ({ slug }) => slug === data.range.slug)) {
            subCategoryPath = `/${locale}${_.get(item, 'url') || _.get(item, 'slug')}`;
            linePath = `/${locale}/range/${subCategoryPath.split('/')[3]}/${_.get(data, 'range.slug')}`;

            labelWithSubCategory = label && label.toUpperCase();
            subCategoryLabel = !!_.get(item, 'label') && item.label.toUpperCase();
          }
          // 該当: MEN > THE GROOMING
          else if (_.get(item, 'url') && item.url.split('/')[3] === data.range.slug) {
            if (!labelWithSubCategory) labelWithSubCategory = label && label.toUpperCase();
            subCategoryLabel = (_.get(data, 'range.subCategory') || _.get(item, 'label')).toUpperCase();

            // 該当: サブカテゴリ「THE GROOMING」 に含まれるもの
            if (data.range.slug.toLowerCase() === 'the-grooming') {
              _.forEach(_.get(data.range, 'productLists'), ({ products }, index) => {
                _.forEach(products, product => {
                  if (product.slug === data.slug) {
                    const tempString = data.range.productLists[index].productType.toUpperCase();
                    if (tempString !== '') lineLabel = tempString.replace(/AND/, '&');

                    subCategoryPath = `/${locale}${_.get(item, 'url') || _.get(item, 'slug')}`;
                    linePath = `/${locale}/range/${subCategoryPath.split('/')[3]}/${_.get(data, 'range.slug')}`;

                    const slugHolder = _.find(item.children, ({ slug, name }) => {
                      if (name.toUpperCase() === lineLabel) return slug;
                    });

                    if (slugHolder && _.get(slugHolder, 'slug')) {
                      linePath = `/${locale}/range/mens/${slugHolder.slug}`;
                    }
                  }
                });
              });
            }
          }
          // 該当: siteOptions.menu の中にある個々の LINE が slug を持たないもの
          else {
            switch (data.range.slug) {
              case 'the-hair-care':
                if (_.get(item, 'key') === 'THE HAIR CARE') {
                  subCategoryLabel = 'HAIR CARE';
                }
                break;
              case 'adenovital':
              case 'airy-flow':
              case 'aqua-intensive':
              case 'ca-ff':
              case 'fuente-forte':
              case 'luminoforce':
              case 'luminogenic':
              case 'sleekliner':
                subCategoryLabel = 'HAIR CARE';
                linePath = `/${locale}/range/hair-care/${data.range.slug}`;
                break;
              default:
                break;
            }
          }
        }
      });
    });
  }

  const isEnabled = data && _.get(data, 'name') && _.get(data, 'range.name') && locale && location && linePath;

  // パンくずリスト表示内容を配列にする
  let breadCrumbMenuItems = [];
  if (isEnabled) {
    if (labelWithoutSubCategory) {
      // TOP > PRODUCTS > カテゴリ > LINE > 商品
      breadCrumbMenuItems = [
        {
          name: 'TOP',
          path: `/${locale}/`,
        },
        // メニュー名を設定する (PRODUCTS)
        {
          name: menuInfo.label,
          path: null,
        },
        // カテゴリ名を設定する (例) HAIR COLOR)
        {
          name: labelWithoutSubCategory,
          path: null,
        },
        // LINE 名を設定する (例) COLORMUSE)
        {
          name: data.range.name.toUpperCase(),
          path: linePath,
        },
        // 商品名を設定する (例) COLORMUSE COLOR CREAM)
        {
          name: data.name.toUpperCase(),
          path: null,
        },
      ];
    } else {
      // TOP > PRODUCTS > カテゴリ > サブカテゴリ > LINE > 商品
      breadCrumbMenuItems = [
        {
          name: 'TOP',
          path: `/${locale}/`,
        },
        // メニュー名を設定する (PRODUCTS)
        {
          name: menuInfo.label,
          path: null,
        },
        // カテゴリ名を設定する (例) HAIR CARE)
        {
          name: labelWithSubCategory,
          path: null,
        },
        // サブカテゴリ名を設定する (例) SUBLIMIC)
        {
          name: _.get(data, 'range.subCategory') ? _.get(data, 'range.subCategory').toUpperCase() : subCategoryLabel,
          path: subCategoryPath,
        },
        // LINE 名を設定する (例) AQUA INTENSIVE)
        {
          name: lineLabel || data.range.name.toUpperCase(),
          path: linePath,
        },
        // 商品名を設定する (例) AQUA INTENSIVE SHAMPOO)
        {
          name: data.name.toUpperCase(),
          path: null,
        },
      ];
    }
  }
  // PRODUCT RANGE を持たない、且つ、SUBLIMIC に商品が直接紐づいているもの
  else {
    if (_.get(data, 'range.key') && data.range.key.toUpperCase() === 'SUBLIMIC') {
      breadCrumbMenuItems = [
        {
          name: 'TOP',
          path: `/${locale}/`,
        },
        // メニュー名を設定する (PRODUCTS)
        {
          name: menuInfo.label,
          path: null,
        },
        // カテゴリ名を設定する (例) HAIR CARE)
        {
          name: labelWithSubCategory,
          path: null,
        },
        // サブカテゴリ名を設定する (例) SUBLIMIC)
        {
          name: data.range.key,
          path: `/${locale}/range/hair-care/sublimic`,
        },
        // 商品名を設定する (例) AQUA INTENSIVE SHAMPOO)
        {
          name: data.name.toUpperCase(),
          path: null,
        },
      ];
    }
  }

  return (
    <React.Fragment>
      {_.get(data, 'slug') && (
        <Helmet
          link={[
            makeCanonicalTagObj(locale, `/${data.contentType}/${data.slug}`),
            ...makeHreflangTagArr(data.region, `/${data.contentType}/${data.slug}`),
          ]}
        />
      )}
      {_.get(data, 'metaData') && (
        <Helmet
          title={_.get(data.metaData, 'title')}
          meta={[
            {
              name: 'description',
              content: _.get(data.metaData, 'description'),
            },
            {
              name: 'keyword',
              content: _.get(data.metaData, 'keyword'),
            },
            // TODO: OGP タグは一旦保留 (2020年01月07日)
            // {
            //   property: 'og:url',
            //   content: _.get(data.metaData, 'url'),
            // },
            // {
            //   property: 'og:type',
            //   content: _.get(data.metaData, 'type'),
            // },
            // {
            //   property: 'og:title',
            //   content: _.get(data.metaData, 'title'),
            // },
            // {
            //   property: 'og:image',
            //   content: _.get(data.metaData, 'image.file.url'),
            // },
            // {
            //   property: 'og:description',
            //   content: _.get(data.metaData, 'description'),
            // },
            // {
            //   property: 'og:site_name',
            //   content: _.get(data.metaData, 'siteName'),
            // },
          ]}
        />
      )}

      <ProductHeroOverview copy={copy} locale={locale} product={data} />

      {(data.notLoaded || data.instructionVideo) && <ProductInstruction copy={copy} video={data.instructionVideo} />}

      {(data.notLoaded || data.benefits) && (
        <ProductBenefits benefits={data.benefits} extended={data.notLoaded || data.instructionVideo} />
      )}

      {(data.notLoaded || data.howTo) && <ProductHowTo copy={copy} howTo={data.howTo} />}

      {data.keyIngredients && (
        <ProductIngredients copy={copy} keyIngredients={data.keyIngredients} otherIngredients={data.otherIngredients} />
      )}

      {(data.notLoaded || data.reviews) && <ProductReviews copy={copy} reviews={data.reviews} />}

      {data.relatedProducts && <ProductRecommendation copy={copy} relatedProducts={data.relatedProducts} />}

      {/* パンくずリスト */}
      {breadCrumbMenuItems.length > 0 ? (
        <BreadCrumbMenu currentPath={window.location.href} items={breadCrumbMenuItems} product={data} />
      ) : (
        <Placeholder w="200px" h="24px" m={window.innerWidth > TABLET_P ? '0 0 20px 40px' : '0 0 20px 20px'} />
      )}
    </React.Fragment>
  );
};

ProductPage.defaultProps = {
  copy: {},
  data: {
    notLoaded: true,
    keyIngredients: [{ key: 1 }, { key: 2 }, { key: 3 }],
    relatedProducts: [{ key: 1 }, { key: 2 }, { key: 3 }, { key: 4 }],
  },
};

ProductPage.propTypes = {
  copy: PropTypes.shape(),
  // 商品情報 (パンくずリストに必要な情報を含む)
  menuInfo: PropTypes.shape({}).isRequired,
  locale: PropTypes.string.isRequired,
  data: PropTypes.shape({
    keyIngredients: PropTypes.arrayOf(PropTypes.shape()),
    relatedProducts: PropTypes.oneOfType([PropTypes.shape(), PropTypes.arrayOf(PropTypes.shape())]),
  }),
  location: PropTypes.shape({}).isRequired,
};

const mapState = state => ({
  copy: state.siteOptions.siteCopyProductPage,
  // 商品情報をキャッシュしておく
  menuInfo: state.siteOptions.menu ? state.siteOptions.menu[3] : {},
  locale: state.locale,
});

export default connect(mapState)(ProductPage);



// WEBPACK FOOTER //
// ./src/pages/ProductPage/ProductPage.js